import { useLoggedUser } from '~/services/user';
import { useUserBankAccountList } from '~/services/user-bank-account';
import { InstitutionSource } from '~/types/institution';
import { UserBankAccountStatus } from '~/types/user-bank-account';

interface UserBankAccountPermissions {
  isLoading: boolean;
  isSibillAccountEnabled: boolean;
  canViewAccount: boolean;
  canInitiatePayments: boolean;
  canManageBeneficiaries: boolean;
  canManageAccountMembership: boolean;
  canManageCards: boolean;
}

/**
 * Returns the logged-in user's bank account permissions for the Swan source.
 *
 * - `isSibillAccountEnabled` is true when the user has an enabled Swan bank account,
 *   which implies an authorised Swan consent exists.
 * - Each permission flag is true only when the Sibill account is enabled AND the
 *   user's `UserBankAccount` record has the corresponding flag set.
 */
export const useUserBankAccountPermissions = (): UserBankAccountPermissions => {
  const loggedUserQuery = useLoggedUser();
  const loggedUser = loggedUserQuery.data;

  const userBankAccountQuery = useUserBankAccountList({
    filters: {
      source: InstitutionSource.SWAN,
      statuses: [UserBankAccountStatus.ENABLED],
      userId: loggedUser?.id,
    },
    enabled: !!loggedUser?.id,
  });
  const userBankAccount = userBankAccountQuery.data?.[0];

  const isSibillAccountEnabled = !!userBankAccount;

  return {
    isLoading: loggedUserQuery.isLoading || userBankAccountQuery.isPending,
    isSibillAccountEnabled,
    canViewAccount: isSibillAccountEnabled && userBankAccount.canViewAccount === true,
    canInitiatePayments: isSibillAccountEnabled && userBankAccount.canInitiatePayments === true,
    canManageBeneficiaries: isSibillAccountEnabled && userBankAccount.canManageBeneficiaries === true,
    canManageAccountMembership: isSibillAccountEnabled && userBankAccount.canManageAccountMembership === true,
    canManageCards: isSibillAccountEnabled && userBankAccount.canManageCards === true,
  };
};
